import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import { MoneyScaled } from '@quickbit/qb-design-system';
import { useQuery } from '@tanstack/react-query';
import { AccountStatus } from './types';

export interface AffiliateUserFilters {
  userId?: string;
  externalReference?: string;
  country?: string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  personalIdNumber?: string;
  onboardingAtLte?: string | null;
  onboardingAtGte?: string | null;
  status?: string | null;
  riskScoreGte?: number | null;
  highRiskUsers?: boolean | null;
}

export interface AffiliateTransactionFilters {
  userId?: string;
}
export interface Pagination {
  page: number;
  size: number;
}

interface AggregatedData {
  count: number;
}

export enum UserType {
  AFFILIATE = 'AFFILIATE',
  DIRECT_ONRAMP = 'DIRECT_ONRAMP',
}

export type AffiliateUser = {
  addressCity: string | null;
  addressCountryCode: string | null;
  addressPostalCode: string | null;
  addressRegion: string | null;
  addressStreetLine1: string | null;
  birthdate: string;
  customerRef: string;
  emailAddress: string;
  firstName: string;
  externalReference: string;
  id: number;
  userType: UserType;
  lastCardCountry: string;
  lastIpAddress: string;
  lastIpAddressCountry: string;
  lastName: string | null;
  lastVerificationCountry: string | null;
  onSanctionsList: boolean | null;
  onboarding: Record<string, any> | null;
  verification: Record<string, any> | null;
  onboardingAt: Date;
  personalIdNumber: string | null;
  phone_Number: string | null;
  status: AccountStatus;
  riskScoreAdjustment: string | null;
  riskScore: number | null;
  uuid: string | null;
};

export interface SearchAffiliateUsersResponse {
  users: AffiliateUser[];
  aggregatedData: AggregatedData;
}

export interface AffiliateUserChange {
  change: Record<string, any>[];
  transactionId: string;
  timestamp: Date;
}

export interface AffiliateUserEvent {
  eventType: string;
  occurredOn: string;
  note?: string | null;
}

export interface OnboardingHistoryResponse {
  onboardingHistory: AffiliateUserChange[];
  events: AffiliateUserEvent[];
}

const queryKeys = {
  all: () => ['all'] as const,
  onboardingHistory: (id: string) =>
    [...queryKeys.all(), 'onboardingHistory', id] as const,
};

export const useGetOnboardingHistory = (id: string) => {
  return useQuery<OnboardingHistoryResponse, ErrorResponse>({
    queryKey: queryKeys.onboardingHistory(id),
    queryFn: async () => {
      return await fetchAPI<OnboardingHistoryResponse>(
        `v1/affiliate-users/${id}/onboarding-history`
      );
    },
  });
};

export const searchAffiliateUsers = async ({
  filters,
  pagination,
}: {
  filters: AffiliateUserFilters;
  pagination: Pagination;
}) => {
  return fetchAPI<SearchAffiliateUsersResponse>('v1/affiliate-users:search', {
    method: 'POST',
    body: {
      filters,
      pagination,
    },
  });
};

export const exportAffiliateUsers = async ({
  filters,
}: {
  filters: AffiliateUserFilters;
}) => {
  const response = await fetchAPI('v1/affiliate-users:export', {
    method: 'POST',
    body: {
      filters,
    },
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  const blob = new Blob(safeChunks, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return blob;
};

export const exportHighRiskUsers = async () => {
  const response = await fetchAPI('v1/export-high-risk-users', {
    method: 'GET',
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  const blob = new Blob(safeChunks, { type: 'text/csv' });
  return blob;
};

export const exportAffiliateTransactions = async (userId: string) => {
  const response = await fetchAPI(`v1/affiliate-users/${userId}/transactions`, {
    method: 'POST',
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  const blob = new Blob(safeChunks, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return blob;
};

interface UpdateUser {
  userType: 'AFFILIATE';
  status?: AccountStatus;
  riskScoreAdjustment?: number;
}

export const updateUser = ({
  userReference,
  riskScoreAdjustment,
  status,
}: {
  userReference: string;
  riskScoreAdjustment?: number | null;
  status?: AccountStatus;
}) => {
  let body: UpdateUser = {
    userType: 'AFFILIATE',
    status,
  };
  if (riskScoreAdjustment != null) {
    body = { ...body, riskScoreAdjustment };
  }
  return fetchAPI(`v1/qb-user/${userReference}`, {
    method: 'PUT',
    body: { ...body },
  });
};

export interface AffiliateTransactionMinimal {
  supportId: string;
  walletId: string;
  requestedAmount: number;
  requestedCurrency: string;
  settledAmount: number;
  settledCurrency: string;
  createdAt: Date;
  updatedAt: Date;
  status: string;
  orderRef: string;
  transactionId?: string;
  pspTransactionId?: string;
  pspId?: string;
  deviceIp?: string;
  rawTransaction?: object;
}
export interface SearchAffiliateTransactionsResponse {
  transactions: AffiliateTransactionMinimal[];
  aggregatedData: { count: number; total: MoneyScaled };
}

export interface SearchAffiliateTransactionsFilters {
  userId: string;
  user_type?: UserType;
}

export const searchAffiliateTransactions = async ({
  filters,
}: {
  filters: SearchAffiliateTransactionsFilters;
}) => {
  const response = await fetchAPI<SearchAffiliateTransactionsResponse>(
    `v1/affiliate-transactions:search`,
    {
      method: 'POST',
      body: {
        filters,
      },
    }
  );

  return {
    ...response,
    transactions: response.transactions.map((transaction) => ({
      ...transaction,
      createdAt: new Date(transaction.createdAt),
      updatedAt: new Date(transaction.updatedAt),
    })),
  };
};
